import { useCallback } from "react"

type DownloadFileFunction = (data: Blob, fileName: string) => void

/**
 * Returns a function to download a file.
 *
 * @returns A function that, when called with file data (Blob)
 * and a filename, initiates a browser download.
 *
 * @example
 * const downloadFile = useDownloadFile();
 *
 * const handleDownloadFile = () => {
 *   const file = new Blob(["Hello, world!"], { type: "text/plain" });
 *   downloadFile(file, "hello.txt")
 * }
 */
export const useDownloadFile = (): DownloadFileFunction => {
  const downloadFile = useCallback((data: Blob, fileName: string): void => {
    const link = document.createElement("a")
    const url = window.URL.createObjectURL(data)

    link.href = url
    link.setAttribute("download", fileName)
    link.style.display = "none" // Make the link invisible

    // Append link element to enable click in some browsers/contexts
    document.body.appendChild(link)

    // Start download
    link.click()

    // Clean up and remove the link
    window.URL.revokeObjectURL(url)
    link.remove()
  }, [])

  return downloadFile
}
